import * as utils from './utils.js';
//name specific functions from utils to use without utils. prefix
const { log, normalizeString, removeAccents, displayLongDistance, createTranslator, childRouteNameLookup } = utils;

import { getAllPlaceData, getReversedPlaceData, getPlaceById, getRouteIdBySlug, getRouteById, getChildRoutePlaceIsAlong, getPlaceAlongRoute, getPlaceIndexAlongRoute } from './mapPlannerShared.js';
import { updateQueryParam, updateMapFromUrl } from './mapPage.js';

const $ = window.jQuery;

const t = createTranslator('common');

// Set global vars
let pathStops = {};

// ────────────────────────────────────────────────────────────────────────────────
// Origin and Destination Select/Stage Stop List Display Functions
// ────────────────────────────────────────────────────────────────────────────────

// - Start with whole route. (Needs to know originId though to calculate relative distances from origin. Challenging once child routes involved. Maybe handle later.)
// - Add children to the route places.
// - Filter from origin (if set)
// - Filter to destination (if set)
// - Save to global var
// - Serve up chunks from global var (to avoid recalculating for every keystroke in search box)
// - Whenever origin or destination changes, start over.

export function setGlobalPathStops(key, data) {
    pathStops[key] = utils.deepFreeze(data);
}
export function getGlobalPathStops(key) {
    return pathStops[key];
}
export function clearGlobalPathStops(key) {
    pathStops[key] = null;
}

/**
 * Filters the places in the route for the select.
 * ONLY LOOKS AT ONE ROUTE AT A TIME. DOES NOT TRAVERSE CHILD ROUTES. This means that, a routePlace could be the split, but not individual child route places, that requires calling this function a 2nd time, with the child's route id.
 * @param {string|number} routeId - The ID of the route to filter. SINGLE ROUTE ONLY. Does not travel through child routes.
 * @param {string} direction - The direction of the route.
 * @returns {Object} The filtered places. (A subset of the route['routeId'].places array.)
 */
export function getRoutePlaces(routeId, direction=null) {
    routeId = parseInt(routeId);

    const workingData = (direction === 'reverse')
        ? getReversedPlaceData()
        : getAllPlaceData();

    // Validate routeId.
    if(!workingData.routes[routeId]) {
        log('Route not found: ', routeId);
        return false;
    }

    // Get all places in the route.
    let filteredPlaces = workingData.routes[routeId].places;

    // Enrich route places with data from places object, but it's frozen, so we need to create a new object to hold the extra vars.
    const enrichedPlaces = [];
    $.each(filteredPlaces, function(index, routePlace) {
        // Copy original object from ROUTES object to a new object.
        const placeItemClone = { ...routePlace };
        
        // Add data from PLACES object to the routePlace object.
        const placeData = workingData.places[routePlace.id];

        placeItemClone.text = placeData.text;
        placeItemClone.slug = placeData.slug;
        placeItemClone.type = placeData.type;
        placeItemClone.services = placeData.services || [];
        placeItemClone.searchText = removeAccents(normalizeString(placeData.text));
        placeItemClone.shortDesc = placeData.short_desc;
        placeItemClone.stampImageUrl = placeData.stamp_image;

        // Add the new object to the filteredPlacesWithVars array.
        enrichedPlaces.push(placeItemClone);
    });

    // log('enrichedPlaces', enrichedPlaces);
    return enrichedPlaces;
}

/**
 * Returns a copy of route places with child route places added to SPLIT (does not mutate input).
 * @param {Array} routePlaces - The places to add the child route places to.
 * @returns {Array} A new array of place objects with children added where applicable.
 */
export function addChildRoutePlaces(routePlaces, direction=null) {
    // Create a copy of the routePlaces array to avoid mutating the original.
    const output = routePlaces.map(function(rp) { return { ...rp }; });
    $.each(output, function(index, routePlace) {
        if(routePlace.hasOwnProperty('childRouteId') && routePlace.hasOwnProperty('intersectType') && routePlace.intersectType === 'split') {
            const childRoutePlaces = getRoutePlaces(routePlace.childRouteId, direction);
            if (childRoutePlaces.length > 0) {
                routePlace.children = childRoutePlaces.slice(1, -1);
            }
        }
    });
    return output;
}

export function sliceRoutePlacesFromOrigin(routePlaces, originId) {
    originId = parseInt(originId);
    // Get the index of the origin place in the routePlaces.
    const originIndex = routePlaces.findIndex(pl => pl.id === originId);
    if(originIndex > -1) {
        // OriginId found! Filter the routePlaces from the origin index.
        return routePlaces.slice(originIndex);
    } else {
        // OriginId not found! Check child routes.
        
        // Loop through routePlaces and check each place's children property for the originId.
        let childRouteId = false;
        let originIndexOnChildRoute = false;
        let childRouteSplitIndex = false;
        $.each(routePlaces, function(index, place) {
            if(place.hasOwnProperty('children') && place.children.length > 0) {
                const childIndex = place.children.findIndex(c => c.id === originId);
                if(childIndex > -1) {
                    originIndexOnChildRoute = childIndex;
                    childRouteId = place.childRouteId;
                    childRouteSplitIndex = index;
                    return false;
                }
            }
        });

        if(childRouteId !== false && originIndexOnChildRoute !== false && childRouteSplitIndex !== false) {
            // OriginId found on child route!
            // Filter the routePlaces from the index of the SPLIT.
            let filteredRoutePlaces = routePlaces.slice(childRouteSplitIndex);
            
            // Filter within the child route places from the origin index.
            // Now that I've sliced the top-level route place, the childRouteSplitIndex will be 0.
            let splitPlace = filteredRoutePlaces[0];
            splitPlace.children = splitPlace.children.slice(originIndexOnChildRoute);

            return filteredRoutePlaces;
        }
        
        // OriginId not found on any route!
        log('OriginId not found on any route: ', originId);
        return false;
    }
}

export function disableRoutePlacesFromOrigin(routePlaces, originId) {
    originId = parseInt(originId);
    // Get the index of the origin place in the routePlaces.
    const originIndex = routePlaces.findIndex(pl => pl.id === originId);
    if(originIndex > -1) {
        // OriginId found! Filter the routePlaces from the origin index.
        $.each(routePlaces, function(index, place) {
            if(index < originIndex) {
                place.disabled = true;
            }
        });
        return routePlaces;
    } else {
        // OriginId not found! Check child routes.
        
        // Loop through routePlaces and check each place's children property for the originId.
        let childRouteId = false;
        let originIndexOnChildRoute = false;
        let childRouteSplitIndex = false;
        $.each(routePlaces, function(index, place) {
            if(place.hasOwnProperty('children') && place.children.length > 0) {
                const childIndex = place.children.findIndex(c => c.id === originId);
                if(childIndex > -1) {
                    originIndexOnChildRoute = childIndex;
                    childRouteId = place.childRouteId;
                    childRouteSplitIndex = index;
                    return false;
                }
            }
        });

        if(childRouteId !== false && originIndexOnChildRoute !== false && childRouteSplitIndex !== false) {
            // OriginId found on child route!
            // Filter the routePlaces from the index of the SPLIT.
            let filteredRoutePlaces = routePlaces;
            $.each(filteredRoutePlaces, function(index, place) {
                if(index < childRouteSplitIndex) {
                    place.disabled = true;
                }
            });
            
            // Filter within the child route places from the origin index.
            // Now that I've sliced the top-level route place, the childRouteSplitIndex will be 0.
            let splitPlace = filteredRoutePlaces[0];
            // splitPlace.children = splitPlace.children.slice(originIndexOnChildRoute);
            $.each(splitPlace.children, function(index, childPlace) {
                if(index < originIndexOnChildRoute) {
                    childPlace.disabled = true;
                }
            });

            return filteredRoutePlaces;
        }
        
        // OriginId not found on any route!
        log('OriginId not found on any route: ', originId);
        return false;
    }
}

export function sliceRoutePlacesToDestination(routePlaces, destinationId) {
    destinationId = parseInt(destinationId);
    // Get the index of the destination place in the routePlaces.
    const destinationIndex = routePlaces.findIndex(pl => pl.id === destinationId);
    if(destinationIndex > -1) {
        // DestinationId found! Filter the routePlaces from the destination index.
        // NOTE: Slice is EXCLUSIVE of the end, so we're adding 1 to include it.
        return routePlaces.slice(0, destinationIndex+1);
    } else {
        // DestinationId not found! Check child routes.

        // Loop through routePlaces and check each place's children property for the destinationId.
        let childRouteId = false;
        let destinationIndexOnChildRoute = false;
        let childRouteSplitIndex = false;
        $.each(routePlaces, function(index, place) {
            if(place.hasOwnProperty('children') && place.children.length > 0) {
                const childIndex = place.children.findIndex(c => c.id === destinationId);
                if(childIndex > -1) {
                    destinationIndexOnChildRoute = childIndex;
                    childRouteId = place.childRouteId;
                    childRouteSplitIndex = index;
                    return false;
                }
            }
        });

        if(childRouteId !== false && destinationIndexOnChildRoute !== false && childRouteSplitIndex !== false) {
            // DestinationId found on child route!
            // Filter the routePlaces from the index of the SPLIT.
            // NOTE: Slice is EXCLUSIVE of the end, so we're adding 1 to include it.
            let filteredRoutePlaces = routePlaces.slice(0, childRouteSplitIndex+1);

            // Filter within the child route places from the destination index.
            // Now that I've sliced the top-level route place, the childRouteSplitIndex will be the last item.
            let splitPlace = filteredRoutePlaces[filteredRoutePlaces.length - 1];
            splitPlace.children = splitPlace.children.slice(0, destinationIndexOnChildRoute+1);

            return filteredRoutePlaces;
        }
        // DestinationId not found on any route!
        log('DestinationId not found on any route: ', destinationId);
        return false;
    }
}
// window.sliceRoutePlacesToDestination = sliceRoutePlacesToDestination;

/**
 * Filters the routePlaces array by index.
 * @param {Object} routePlaces - The places to filter.
 * @param {integer|null} offset - The offset of the places to return.
 * @param {integer|null} limit - The limit of the places to return.
 * @returns {Object} The filtered places. (A subset of the route['routeId'].places array.)
 */
export function filterRoutePlacesByIndex(routePlaces, offset=null, limit=null) {
    if(offset && parseInt(offset) > -1) {
        routePlaces = routePlaces.slice(offset);
    }
    if(limit && parseInt(limit) > -1) {
        routePlaces = routePlaces.slice(0, limit);
    }
    return routePlaces;
}
// window.filterRoutePlacesByIndex = filterRoutePlacesByIndex;


/**
 * Filters the places in the route by search query. AND REMOVES THEM FROM THE ARRAY.
 * NO LONGER USED, REPLACED BY hideRoutePlacesBySearchQuery() WHICH DOES THE SAME BUT ONLY MARKS THEM AS HIDDEN (TO MAINTAIN INDEX)
 * @param {Array} routePlaces - The places to filter.
 * @param {string} query - The search query to filter by.
 * @returns {Array} The filtered places.
 */
export function filterRoutePlacesBySearchQuery(routePlaces, query='') {
    query = removeAccents(normalizeString(query));
    if(query.length > 0) {
        let filteredPlaces = routePlaces.filter(function(rp) {
            // Search routePlaces searchText for query.
            if (rp.searchText && rp.searchText.includes(query)) {
                return true;
            }
            // Search routePlaces's children's searchText for query. Keep the parent Split visible if any child matches the query.
            if (rp.hasOwnProperty('children') && rp.children.length > 0 && rp.children.some(function(c) {
                return c.searchText && c.searchText.includes(query); 
            })) {
                return true;
            }
            return false;
        });

        // Now that we've filtered the top-level, loop through those results and filter the children
        $.each(filteredPlaces, function(index, rp) {
            if (rp.hasOwnProperty('children') && rp.children.length > 0) {
                rp.children = rp.children.filter(function(c) {
                    return c.searchText && c.searchText.includes(query);
                });
            }
        });

        return filteredPlaces;
    } else {
        return routePlaces;
    }
}

export function hideRoutePlacesBySearchQuery(routePlaces, query='') {
    query = removeAccents(normalizeString(query));
    log('hideRoutePlacesBySearchQuery', query);
    if(query.length > 0) {
        routePlaces.forEach(function(rp) {
            rp.hidden = true;
            if ((rp.type !== 'intersection') && (rp.hasOwnProperty('searchText') && rp.searchText.includes(query))) {
                // Place is visible.
                rp.hidden = false; // Matches query
                // log('Place is visible: ', rp.text);
            }
            if (rp.hasOwnProperty('children') && rp.children.length > 0 && rp.children.some(function(c) {
                return c.searchText && c.searchText.includes(query); 
            })) {
                // Place is visible.
                rp.hidden = false; // A child matches query
                // log('Place is visible bcz of child match: ', rp.text);
            }
        });

        // Now that we've filtered the top-level, loop through those results and filter the children
        $.each(routePlaces, function(index, rp) {
            if (rp.hasOwnProperty('children') && rp.children.length > 0) {
                $.each(rp.children, function(cindex, c) {
                    if(c.hasOwnProperty('searchText') && c.searchText.includes(query)) {
                        // Child is visible.
                    } else {
                        c.hidden = true;
                    }
                });
            }
        });
    }
    return routePlaces;
}

/**
 * Renders a list of filtered places.
 * @param {Object} filteredPlaces - The filtered places to render.
 * @param {string} listType - The type of list to render. 'origin' or 'destination' or 'stoplist'.
 * @param {integer|null} selectedPlaceId - The ID of the place that is pre-selected.
 * @param {integer|null} originId - The ID of the origin place. (For labeling)
 * @param {integer|null} destinationId - The ID of the destination place. (For labeling)
 * @param {array|null} selectedViaIds - The IDs of the via route toggles that are pre-selected. USED WHEN: Destination select popup is opened and origin is on a child route.
 * @param {integer|null} primaryRouteId - The ID of the primary route. Used to determine if the pre-selected place is on a child route.
 * @returns {Object} The rendered list of places.
 * 
 * 
 * USAGE: $container.empty().append(renderFilteredPlaceList(filteredPlaces)); (filteredPlaces is the output of filterRoutePlaces())
 * Whenever a route is selected (or direction is changed), update the origin select with the origin places.
 * Whenever an origin is selected, update the destination select with the destination places.
 * This way, the selects are ready to display before the user clicks to open them.
 * Per chatgpt: single append per render. Data is cached. No DOM bloat.
 */
export function renderFilteredPlaceList(filteredPlaces, listType='origin', selectedPlaceId=null, originId=null, destinationId=null, selectedViaIds=null, primaryRouteId=null, isSearch=false, showStepIndicators=true, allowToggles=true, showDistances=true, hideDistDiff=false) {
    log('renderFilteredPlaceList', filteredPlaces, listType, selectedPlaceId, originId, destinationId, selectedViaIds, primaryRouteId, isSearch);
    
    // Do without jquery for better performance.

    // Initialize selectedViaIds array if not set.
    if(!selectedViaIds) {
        selectedViaIds = [];
    }
    // Initialize running checked via diffs.
    let runningCheckedViaDiffs = 0;

    // If selectedPlaceId is set, check if it's on a child route. If so, set the selectedPlaceChildRouteId variable.
    // USED WHEN: Pre-selected origin or destination is on a child route.
    let selectedPlaceChildRouteId = false;
    if(selectedPlaceId && primaryRouteId) {
        selectedPlaceChildRouteId = getChildRoutePlaceIsAlong(primaryRouteId, selectedPlaceId);
        if(selectedPlaceChildRouteId) {
            selectedPlaceChildRouteId = parseInt(selectedPlaceChildRouteId);
        }
    }

    // Also, in case of destination select - check if origin is on a child route. If so, set the originChildRouteId variable.
    // USED WHEN: Destination select popup (or stoplist) is opened and origin is on a child route.
    let originChildRouteId = false;
    if((listType === 'destination' || listType === 'stoplist') && originId && primaryRouteId) {
        originChildRouteId = getChildRoutePlaceIsAlong(primaryRouteId, originId);
        if(originChildRouteId) {
            // Origin is on child route.
            originChildRouteId = parseInt(originChildRouteId);
        }
    }
    log('originChildRouteId: ', originChildRouteId);

    // In case of stoplist, check if destination is on a child route. If so, set the destinationChildRouteId variable so we can disable toggles correctly.
    // Not needed for destination select bcz it always runs to the end of the route - and no routes end on a child route.
    let destinationChildRouteId = false;
    let destinationShadowRouteId = false;
    if(listType === 'stoplist' && destinationId && primaryRouteId) {
        destinationChildRouteId = getChildRoutePlaceIsAlong(primaryRouteId, destinationId);
        if(destinationChildRouteId) {
            // Destination is on child route.
            destinationChildRouteId = parseInt(destinationChildRouteId);
        } else {
            // Destination on primary route.
            // Check if it's in the shadow of a child route.
            const destinationPlace = filteredPlaces[filteredPlaces.length - 1]; // Just use last place.
            if(destinationPlace.shadowId) {
                destinationShadowRouteId = destinationPlace.shadowId;
            }
        }
    }
    
    // Create blank container to hold the rendered places.
    const fragment = document.createDocumentFragment();
    
    // Get the templates.
    let singleItemTemplate = document.getElementById('placeSelectSingleItemTemplate').content.cloneNode(true);
    let childRouteStartTemplate = document.getElementById('placeSelectChildRouteStartTemplate').content.cloneNode(true);
    let childRouteEndTemplate = document.getElementById('placeSelectChildRouteEndTemplate').content.cloneNode(true);
    const singleServiceIconBadgeTemplate = document.getElementById('singleServiceIconBadge');

    // Remove step indicators from templates if needed
    if(!showStepIndicators) {
        singleItemTemplate.querySelector('.step-indicator')?.remove();
        singleItemTemplate.querySelector('.step-body')?.classList.add('w-100');
        childRouteStartTemplate.querySelector('.step-indicator')?.remove();
        childRouteStartTemplate.querySelector('.step-body')?.classList.add('w-100');
        childRouteEndTemplate.querySelector('.step-indicator')?.remove();
        childRouteEndTemplate.querySelector('.step-body')?.classList.add('w-100');
    }
    // const categoryLookup = getLookupTableForServices();

    // Loop through the filtered places and render each place based on type.
    $.each(filteredPlaces, function(index, routePlace) {
        
        // If a place is hidden by search query, create a dummy li instead of skipping, to maintain index count so it syncs with data array..
        if(routePlace.hasOwnProperty('hidden') && routePlace.hidden === true) {
            const dummyLi = document.createElement('li');
            dummyLi.classList.add('d-none');
            // dummyLi.setAttribute('data-place-id', routePlace.id);
            fragment.appendChild(dummyLi);
            return true;
        }
        // log('rendering place: ', index, routePlace.text);

        if(routePlace.type === 'intersection') {
            if(routePlace.intersectType === 'split') {
                // log('Split place: ', routePlace);

                // Copy the child route start <li> item.
                const clone = childRouteStartTemplate.cloneNode(true);

                // Maybe expand the child route if it's the first place or if it's a selected via route.
                let childRouteToggleChecked = false;
                let childRouteExpanded = false;
                if(selectedViaIds.includes(routePlace.childRouteId) || selectedPlaceChildRouteId === routePlace.childRouteId || originChildRouteId === routePlace.childRouteId || destinationChildRouteId === routePlace.childRouteId) {
                    childRouteToggleChecked = true;
                    childRouteExpanded = true;
                }

                // Disable the toggle (FORCED CHECKED) if the origin or destination is on this child route
                let childRouteToggleDisabled = false;
                if(!allowToggles) {
                    childRouteToggleDisabled = true;
                }
                if(originChildRouteId === routePlace.childRouteId || destinationChildRouteId === routePlace.childRouteId) {
                    childRouteToggleDisabled = true;
                }

                // Disable the toggle (FORCED UNCHECKED) if the destination is in the shadow of this child route (not relevant to origins bcz they come after the split).
                if(routePlace.childRouteId && routePlace.childRouteId === destinationShadowRouteId) {
                    childRouteToggleDisabled = true;
                }

                // If it's a search, always expand the child routes
                if(isSearch) {
                    childRouteExpanded = true;
                    // childRouteToggleDisabled = false;
                }

                let showChildDistanceDiff = true;
                if(hideDistDiff === true) {
                    showChildDistanceDiff = false;
                }

                const li = renderChildRouteStartItem(clone.firstElementChild, routePlace, childRouteToggleChecked, childRouteExpanded, childRouteToggleDisabled, showChildDistanceDiff);
                
                // Render the individual child stops of the child route.
                if(routePlace.hasOwnProperty('childRouteId') && routePlace.childRouteId > 0) {                    
                    const childRoutePlaceListContainer = li.querySelector('[data-label="child-route-place-list-container"]');
                    if(childRoutePlaceListContainer) {
                        if(routePlace.hasOwnProperty('children') && routePlace.children.length > 0) {
                            for (let childIndex = 0; childIndex < routePlace.children.length; childIndex++) {
                                const childPlace = routePlace.children[childIndex];

                                // If a child place is hidden by search query, just skip it.
                                if(childPlace.hasOwnProperty('hidden') && childPlace.hidden === true) {
                                    continue;
                                }

                                // log('rendering child place: ', childPlace.text);

                                // Create array of service categories for this place
                                // const serviceCatsArray = [...new Set(
                                //     routePlace.services.map(item => categoryLookup[item]).filter(Boolean)
                                // )];
                                const filteredServices = filterMasterServicesList(routePlace.services || []);
                                const serviceCatsArray = Object.keys(filteredServices);

                                // Whether this place is the origin or destination.
                                let isOrigin = false;
                                let isDestination = false;
                                let isFirst = false;
                                let isLast = false;
                                if(childIndex === 0) {
                                    isFirst = true;
                                }
                                if(childIndex === routePlace.children.length - 1) {
                                    isLast = true;
                                }
                                
                                if(originId && childPlace.id === originId) {
                                    isOrigin = true;
                                }
                                if(destinationId && childPlace.id === destinationId) {
                                    isDestination = true;
                                }

                                let isDisabled = true;
                                const childRouteId = routePlace.childRouteId; // Get the current child route id from the parent place.
                                if((childRouteId && selectedViaIds.includes(childRouteId)) || selectedPlaceChildRouteId === childRouteId || originChildRouteId === childRouteId || destinationChildRouteId === childRouteId) {
                                    isDisabled = false;
                                }

                                // Get the distance from the origin to this place.
                                // = Baseline + selectedViaIds above it differences.
                                let distanceFromOrigin = null;
                                if(showDistances) {
                                    distanceFromOrigin = childPlace.baselineDistance || 0;
                                    distanceFromOrigin += runningCheckedViaDiffs;
                                }

                                const clone = singleItemTemplate.cloneNode(true);
                                const child_li = renderSingleItem(clone.firstElementChild, singleServiceIconBadgeTemplate, childPlace, listType, serviceCatsArray, distanceFromOrigin, selectedPlaceId, isOrigin, isDestination, isFirst, isLast, isDisabled, null);
                                
                                //Updating the existing childRoutePlaceListContainer node, not the same as creating a new one.
                                // TODO: IS THIS ADDING NODES INDIVIDUALLY OR ALL AT ONCE? SHOULD BE ALL AT ONCE.
                                // SORTA BOTH, LOOKS LIKE. ADDING INDIVIDUALLY TO A CONTAINER WITHIN A FRAGMENT.
                                childRoutePlaceListContainer.append(child_li);
                            }
                        } else {
                            // No children found for this child route.
                            // log('No children found for this child route: ', routePlace.childRouteId);
                            // Add a placeholder node here to the childRoutePlaceListContainer.
                            // E.g. "No places found along this route."
                        }
                    }
                }
                
                // Append the new childRouteStartItem (with children) to the fragment.
                fragment.appendChild(li);

                // Update runningCheckedViaDiffs.
                // Note: We do this AFTER rendering the item so we don't include the differential distance in the item's distance.
                // This would affect the join also but we aren't displaying distance for the join.
                if(selectedViaIds.includes(routePlace.childRouteId)) {
                    runningCheckedViaDiffs += routePlace.childDistDiff;
                }

            } else if(routePlace.intersectType === 'join') {
                const clone = childRouteEndTemplate.cloneNode(true);
                const li = renderChildRouteEndItem(clone.firstElementChild, routePlace);
                fragment.appendChild(li);
            } else {
                log('Shouldnt happen: Unknown intersect type: ', routePlace.intersectType);
                return false;
            }
        } else {
            // Regular place item

            // HIDE PLACES THAT ARE IN THE SHADOW OF THE ORIGIN's CHILD ROUTE.
            if(routePlace.shadowId && originChildRouteId && routePlace.shadowId === originChildRouteId) {
                // This place is in the shadow of the origin child route. Skip it in this $.each loop.
                // Create dummy li instead of skipping, to maintain index count so it syncs with data array..
                const dummyLi = document.createElement('li');
                dummyLi.classList.add('d-none');
                // dummyLi.setAttribute('data-place-id', routePlace.id);
                fragment.appendChild(dummyLi);
                return true;
            }

            // const serviceCatsArray = [...new Set(
            //     routePlace.services.map(item => categoryLookup[item]).filter(Boolean)
            // )];
            const filteredServices = filterMasterServicesList(routePlace.services || []);
            const serviceCatsArray = Object.keys(filteredServices);

            // Whether this place is the origin or destination.
            let isOrigin = false;
            let isDestination = false;

            let isFirst = false;
            let isLast = false;
            if(index === 0) {
                isFirst = true;
            }
            if(index === filteredPlaces.length - 1) {
                isLast = true;
            }

            if(originId && routePlace.id === originId) {
                isOrigin = true;
            }
            if(destinationId && routePlace.id === destinationId) {
                isDestination = true;
            }

            let isDisabled = false;
            if(
                (routePlace.shadowId && selectedViaIds.includes(routePlace.shadowId)) || 
                selectedPlaceChildRouteId === routePlace.shadowId || 
                originChildRouteId === routePlace.shadowId || 
                destinationChildRouteId === routePlace.shadowId
            ) {
                isDisabled = true;
            }
            if(routePlace.hasOwnProperty('disabled') && routePlace.disabled === true) {
                isDisabled = true;
            }
            
            // Get the distance from the origin to this place.
            // = Baseline + selectedViaIds above it differences.
            let distanceFromOrigin = null;
            if(showDistances) {
                distanceFromOrigin = routePlace.baselineDistance || 0;
                distanceFromOrigin += runningCheckedViaDiffs;
            }

            const clone = singleItemTemplate.cloneNode(true);
            const li = renderSingleItem(clone.firstElementChild, singleServiceIconBadgeTemplate, routePlace, listType, serviceCatsArray, distanceFromOrigin, selectedPlaceId, isOrigin, isDestination, isFirst, isLast, isDisabled);
            fragment.appendChild(li);
        }
    });

    return fragment;
}

/**
 * Renders a single item from the single item template.
 * @param {Object} li - The li element to render into.
 * @param {Object} routePlace - The route place to render.
 * @param {string} listType - The type of list to render. 'origin' or 'destination' or 'stoplist'.
 * @returns {Object} The rendered li element.
 * 
 * For use with the renderFilteredPlaceList function as a helper function.
 * Basically Find/replace the place data in the template with the route place data.
 */
function renderSingleItem(li, singleServiceIconBadgeTemplate, routePlace, listType, serviceCatsArray=[], distanceFromOrigin=null, selectedPlaceId=null, isOrigin=false, isDestination=false, isFirst=false, isLast=false, isDisabled=false) {
    // log('renderSingleItem', routePlace);
    // Origin/Destination select variables
    const radioSelect = li.querySelector('input[data-label="radio-select"]');
    radioSelect?.setAttribute('value', routePlace.id);

    // Set pre-selected place if applicable.
    if(selectedPlaceId && selectedPlaceId === routePlace.id) {
        radioSelect?.setAttribute('checked', 'checked');
        li.classList.add('scroll-to-preselected-place');
        if(listType === 'stoplist') {
            li.classList.add('spoofChecked');
        }
    }

    // Disable the radio input and the list item if the place is disabled.
    if(isDisabled) {
        radioSelect?.setAttribute('disabled', 'disabled');
        li.classList.add('place-disabled'); // For stoplists since they don't have <input>s
        // li.classList.add('no-hover');
        // li.classList.add('opacity-50');
        // li.classList.add('pe-none');
    }

    // Services
    if(routePlace.services && routePlace.services.length > 0) {
        li.setAttribute('data-services', routePlace.services.join(','));
        const serviceBadgesContainer = li.querySelector('[data-label="serviceBadgesContainer"]');
        if(serviceBadgesContainer) {
            const fragment = document.createDocumentFragment();
            for (let index = 0; index < serviceCatsArray.length; index++) {
                const serviceCat = serviceCatsArray[index];
                const clone = singleServiceIconBadgeTemplate.content.cloneNode(true);
                const i = clone.querySelector('i');
                i.classList.add(window.ironman.appConfig.servicesOffered[serviceCat].icon);
                fragment.appendChild(clone);
            }
       
            serviceBadgesContainer.append(fragment);
        }
    }

    // Place Name
    const placeName = li.querySelector('[data-label="place-name"]');
    if(
        placeName &&
        routePlace.text
    ) {
        placeName.textContent = routePlace.text;
    }

    // Place Type
    const placeType = li.querySelector('[data-label="place-type"]');
    if(
        placeType &&
        routePlace.type
    ) {
        // Set the place type text from the app config, or fallback to the original type text.
        placeType.textContent = window.ironman.appConfig?.placeTypes?.places?.children?.[routePlace.type]?.name ?? routePlace.type;
    }

    // Place Short Description
    const placeShortDescription = li.querySelector('[data-label="place-short-description"]');
    if(
        placeShortDescription &&
        routePlace.shortDesc
    ) {
        placeShortDescription.textContent = routePlace.shortDesc;
    }

    // Place ID
    li.setAttribute('data-place-id', routePlace.id);
    // const placeId = li.querySelector('[data-place-id]');
    // if(
    //     placeId &&
    //     routePlace.id
    // ) {
    //     placeId.setAttribute('data-place-id', routePlace.id);
    // }

    // Shadow Route ID
    if(routePlace.shadowId) {
        li.classList.add('hasShadowRoute');
        li.setAttribute('data-shadow-route-id', routePlace.shadowId);
    }

    // Place Slug
    const placeSlug = li.querySelector('[data-place-slug]');
    if(placeSlug && routePlace.slug) {
        placeSlug.setAttribute('data-place-slug', routePlace.slug);
    }

    // Place Stamp Image
    const placeStampImage = li.querySelector('[data-label="placeStampImage"]');
    if(placeStampImage && routePlace.stampImageUrl) {
        placeStampImage.setAttribute('src', routePlace.stampImageUrl);
    }

    // Destination only variables
    if(listType === 'destination' || listType === 'stoplist') {
        // Place Distance
        if(distanceFromOrigin !== null) {
            const placeDistance = li.querySelector('[data-label="place-distance"]');
            if(placeDistance) {
                placeDistance.textContent = displayLongDistance(distanceFromOrigin || 0);
            }
        }
        

        // Place Asc
        // const placeAsc = li.querySelector('[data-label="place-asc"]');
        // if(
        //     placeAsc &&
        //     routePlace.ascFromOrigin
        // ) {
        //     placeAsc.textContent = routePlace.ascFromOrigin;
        // }

        // // Place Desc
        // const placeDesc = li.querySelector('[data-label="place-desc"]');
        // if(
        //     placeDesc &&
        //     routePlace.descFromOrigin
        // ) {
        //     placeDesc.textContent = routePlace.descFromOrigin;
        // }

        // Origin/Destination Badge
        if(isOrigin || isDestination) {
            const placeOriginDestinationBadge = li.querySelector('[data-label="place-origin-destination-badge"]');
            if(isOrigin) {
                li.classList.add('isOrigin');
                if(placeOriginDestinationBadge) {
                    placeOriginDestinationBadge.classList.add('ironman-badge', 'primary', 'small');
                    placeOriginDestinationBadge.innerHTML = '<span>'+t('origin')+'</span>';
                }
            }

            if(isDestination) {
                li.classList.add('isDestination');
                if(placeOriginDestinationBadge) {
                    placeOriginDestinationBadge.classList.add('ironman-badge', 'primary', 'small');
                    placeOriginDestinationBadge.innerHTML = '<span>'+t('destination')+'</span>';
                }
            }

            if(listType !== 'stoplist') {
                // Disable the radio input and the list item.
                radioSelect?.setAttribute('disabled', 'disabled');
                li.classList.add('no-hover');
            }
        }

        if(isFirst || isLast) {
            const stepIndicator = li.querySelector('.step-indicator');
            if(stepIndicator) {
                if(isFirst) {
                    stepIndicator.classList.add('step-indicator-first');
                }
                if(isLast) {
                    stepIndicator.classList.add('step-indicator-last');
                }
            }
        }
    }

    if(listType === 'stoplist') {
        // Remove pointer cursor
        const label = li.querySelector('label.placeSelectSingleItemLabel');
        if(label) {
            label.classList.remove('cursor-pointer');
        }
        
        // Make Place name a link to the place page.
        const placeName = li.querySelector('a[data-label="place-name"]');
        if(placeName) {
            placeName.setAttribute('href', '/place/'+routePlace.slug+'/');
            // placeName.setAttribute('target', '_blank');

            // Add hover effect underline for stoplist only. In select popups, it's not a link.
            placeName.classList.add('hover-effect-underline');
        }

        // Remove input field.
        const input = li.querySelector('input[type="radio"][name="placeSelect"]');
        if(input) {
            input.remove();
        }
    }
   
    return li;
}

function renderChildRouteStartItem(li, routePlace, childRouteToggleChecked=false, childRouteExpanded=false, childRouteToggleDisabled=false, showChildDistanceDiff=true) {
    // Set the value of the selectedChildRoutes input to the childRouteId.
    const selectedChildRoutes = li.querySelector('input[name="selectedChildRoutes"]');
    if(selectedChildRoutes && routePlace.childRouteId) {
        selectedChildRoutes.setAttribute('value', routePlace.childRouteId);
    }

    // Child Route ID
    if(routePlace.childRouteId) {
        li.setAttribute('data-child-route-id', routePlace.childRouteId);
    }

    // Hook up the collapse with unique ids.
    const childRouteCollapseToggle = li.querySelector('[data-label="child-route-collapse-toggle"]');
    if(childRouteCollapseToggle && routePlace.childRouteId) {
        childRouteCollapseToggle.setAttribute('data-bs-target', `#collapse-child-route-${routePlace.childRouteId}`);
    }
    const childRouteCollapseContainer = li.querySelector('[data-label="child-route-collapse-container"]');
    if(childRouteCollapseContainer && routePlace.childRouteId) {
        childRouteCollapseContainer.setAttribute('id', `collapse-child-route-${routePlace.childRouteId}`);
    }

    // Child route name
    const childRouteNameField = li.querySelector('[data-label="child-route-name"]');
    if(
        childRouteNameField &&
        routePlace.intersect
    ) {
        // Get the child route's name from the global places data.
        // const childRouteData = getRouteById(routePlace.childRouteId);
        const childRouteSlug = routePlace.intersect;
        
        // Get the child route's name from the translated global array.
        let childRouteName = childRouteNameLookup(childRouteSlug);
        
        childRouteNameField.textContent = childRouteName || childRouteSlug; // Fallback to slug if not found (shouldn't happen).
    
    }

    // Child route distance diff (meters)
    if(showChildDistanceDiff) {
        const childRouteDistanceDiffContainer = li.querySelector('[data-label="child-route-distance-diff"]');
        if(childRouteDistanceDiffContainer && routePlace.childDistDiff) {
            const sign = routePlace.childDistDiff > 0 ?  t('longer') : t('shorter');
            childRouteDistanceDiffContainer.textContent = displayLongDistance(Math.abs(routePlace.childDistDiff)) + ' ' + sign;
        }
    }

    // Child route toggle
    const childRouteToggle = li.querySelector('[data-label="child-route-toggle"]');
    
    // Child route toggle defaults
    if(childRouteExpanded) {
        childRouteCollapseContainer?.classList.add('show');
        childRouteCollapseToggle?.classList.remove('collapsed');
    }
    if(childRouteToggleChecked) {
        childRouteToggle?.setAttribute('checked', 'checked');
    }
    if(childRouteToggleDisabled) {
        childRouteToggle?.setAttribute('disabled', 'disabled');
    }
    if(childRouteToggleDisabled && childRouteToggleChecked === false) {
        li.classList.add('item-disabled');
    }

    return li;
}

function renderChildRouteEndItem(li, routePlace) {
    // Child route name
    const childRouteNameField = li.querySelector('[data-label="child-route-name"]');
    if(
        childRouteNameField &&
        routePlace.intersect
    ) {
        // Get the child route's name from the translated global array.
        const childRouteName = childRouteNameLookup(routePlace.intersect);
        childRouteNameField.textContent = childRouteName || routePlace.intersect; // Fallback to slug if not found (shouldn't happen).
    }
    return li;
}



// Handle clicks of read more links in place lists
$(document).on('click', '[data-action="showFullPlaceListItemContent"]', function(e) {
    e.preventDefault();
    const $link = $(this);
    const placeId = $link.closest('li[data-place-id]').data('place-id');
    const purpose = $link.closest('ul.place-list-container').data('purpose') || null;
    const placeData = getPlaceById(placeId);
    log('showFullPlaceListItemContent: placeData: ', placeData);
    if(placeData) {
        const template = document.getElementById('placeSelectSingleItemMoreContentTemplate');
        const clone = template.content.firstElementChild.cloneNode(true);
        
        // Place Short Description
        if(
            clone.querySelector('[data-label="place-short-description"]') &&
            placeData.short_desc
        ) {
            clone.querySelector('[data-label="place-short-description"]').textContent = placeData.short_desc;
        }

        // Services list
        if(placeData.services && placeData.services.length > 0) {
            // Get the filtered services list.
            const filteredServices = filterMasterServicesList(placeData.services || []);
            log('filteredServicesArray: ', filteredServices);
            const serviceCatsArray = Object.keys(filteredServices);

            // Render the services list.
            const servicesList = clone.querySelector('[data-label="place-services-list"]');
            if(servicesList) {
                const singleServiceCatTemplate = document.getElementById('placeSelectSingleItemMoreContent_SingleServiceListItemTemplate').content.firstElementChild.cloneNode(true);
                const fragment = document.createDocumentFragment();
                for (let index = 0; index < serviceCatsArray.length; index++) {
                    const serviceSlug = serviceCatsArray[index];
                    const service = filteredServices[serviceSlug];
                    
                    const catClone = singleServiceCatTemplate.cloneNode(true);
                    catClone.querySelector('[data-label="place-services-list-item-icon"]').classList.add(service.icon);
                    // Set different # link for accommodation vs services.
                    if(serviceSlug === 'sleep') {
                        catClone.querySelector('[data-label="place-services-list-item-name"]').setAttribute('href', '/place/'+placeData.slug+'/#s-accommodation');
                    } else {
                        catClone.querySelector('[data-label="place-services-list-item-name"]').setAttribute('href', '/place/'+placeData.slug+'/?listing_types='+serviceSlug+'#s-services');
                    }
                    catClone.querySelector('[data-label="place-services-list-item-name"]').textContent = service.name;
                    catClone.querySelector('[data-label="place-services-list-item-child-list"]').innerHTML = '';
                    const children = Object.keys(service.children);
                    for (let index = 0; index < children.length; index++) {
                        const childSlug = children[index];
                        const child = service.children[childSlug];
                        catClone.querySelector('[data-label="place-services-list-item-child-list"]').innerHTML += '<span>'+child.name+'</span>';
                        if (index < children.length - 1) {
                            catClone.querySelector('[data-label="place-services-list-item-child-list"]').innerHTML += '<span class="fs-xl lh-0 text-body-tertiary opacity-50">&bull;</span>';
                        }
                    }
                    fragment.appendChild(catClone);
                }
                servicesList.append(fragment);
            }
        } else {
            // No services here. Hide the services container.
            clone.querySelector('[data-label="place-services-container"]').classList.add('d-none');
        }
        
        // Set Button based on purpose of the list.
        if (purpose) {
            const button = clone.querySelector('[data-label="actionButton"]');
            if (purpose === 'destinationSelect') {
                button.classList.remove('d-none');
                button.setAttribute('data-action', 'destinationSelectModal_submit');
                button.setAttribute('data-place-id', placeData.id);
                button.querySelector('[data-label="actionButtonText"]').textContent = t('selectDestination');
            
            } else if (purpose === 'originSelect') {
                button.classList.remove('d-none');
                button.setAttribute('data-action', 'originSelectModal_submit');
                button.setAttribute('data-place-id', placeData.id);
                button.querySelector('[data-label="actionButtonText"]').textContent = t('selectOrigin');
            }
        }
        
        clone.querySelector('[data-label="explore-place-link"]').setAttribute('href', '/place/'+placeData.slug+'/');
        clone.querySelector('[data-label="explore-place-name"]').textContent = placeData.text;
        // template.querySelector('[data-place-id]')?.setAttribute('data-place-id', placeData.id);
        // template.querySelector('[data-place-slug]')?.setAttribute('data-place-slug', placeData.slug);

        $link.closest('[data-label="swapping-content-block"]').empty().append(clone);
        return true;
    }
    log('placeData not found: ', placeId);
    return false;
});

$(document).on('click', '[data-action="hideFullPlaceListItemContent"]', function(e) {
    e.preventDefault();
    const $link = $(this);
    const placeId = $link.closest('li[data-place-id]').data('place-id');
    const placeData = getPlaceById(placeId);
    log('placeData', placeData);
    if(placeData) {
        const template = document.getElementById('placeSelectSingleItemLessContentTemplate');
        const clone = template.content.firstElementChild.cloneNode(true);
        
        // Place Short Description
        if(
            clone.querySelector('[data-label="place-short-description"]') &&
            placeData.short_desc
        ) {
            clone.querySelector('[data-label="place-short-description"]').textContent = placeData.short_desc;
        }
        $link.closest('[data-label="swapping-content-block"]').empty().append(clone);
        return true;
    } else {
        log('placeData not found: ', placeId);
        return false;
    }
});


/**
 * Builds a lookup table for service categories from the global app config.
 * @returns {Object} The lookup table. (A map of service child keys to service category keys.)
 */
// function getLookupTableForServices() {
//     const categoryLookup = {};
//     for (const [categoryKey, category] of Object.entries(window.ironman.appConfig.servicesOffered)) {
//         for (const childKey of Object.keys(category.children)) {
//             categoryLookup[childKey] = categoryKey;
//         }
//     }
//     return categoryLookup;
// }

/**
 * Filters the master services list to only include the included items.
 * @param {Array} includedItems - The items to include.
 * @returns {Object} The filtered services list. (A map of service category keys to service category objects.)
 * Usage: filterMasterServicesList(['hotel', 'restaurant', 'cafe']) will return a list of service categories that include the hotel, restaurant, and cafe categories.
 * 
 */
export function filterMasterServicesList(includedItems=[]) {
    const includedSet = new Set(includedItems);
    const result = {};

    for (const [categoryKey, category] of Object.entries(window.ironman.appConfig.servicesOffered)) {

        const matchingChildren = Object.fromEntries(
            Object.entries(category.children)
                .filter(([childKey]) => includedSet.has(childKey))
        );

        // only include category if it has matching children
        if (Object.keys(matchingChildren).length > 0) {
            result[categoryKey] = {
                ...category,
                children: matchingChildren
            };
        }
    }

    return result;
}

// Handle toggling of child route toggle inputs.
$(document).on('change', '[data-action="toggleChildRoute"]', function(e) {
    e.preventDefault();
    const $input = $(this);
    const toggleChecked = $input.is(':checked');
    const childRouteId = $input.val();
    log('toggleChildRoute: childRouteId: ', childRouteId);
    let listType = null;
    let placement = null;
    
    // Traverse up the DOM to find the place list container.
    const $placeListContainer = $input.closest('ul.place-list-container');
    if($placeListContainer.length > 0) {
        listType = $placeListContainer.attr('data-list-type');
        log('listType: ', listType);
        placement = $placeListContainer.attr('data-placement');
        log('placement: ', placement); // To assign actions on the map or in a planner stage.
    }

    // 1. SHOW/HIDE INACCESSIBLE PLACES.
    if(toggleChecked) {
        // Disable the shadow places on the primary route.
        const $shadowPlaces = $placeListContainer.find('li.placeSelectSingleItem.hasShadowRoute').filter(`[data-shadow-route-id="${childRouteId}"]`);
        if($shadowPlaces.length > 0) {
            $shadowPlaces.each(function() {
                const $place = $(this);
                $place.addClass('place-disabled');
                $place.find('label.placeSelectSingleItemLabel input[name="placeSelect"]').attr('disabled', 'disabled');

                // If any of the shadow places are selected, unselect them.
                if($place.find('label.placeSelectSingleItemLabel input[name="placeSelect"]').is(':checked')) {
                    $place.find('label.placeSelectSingleItemLabel input[name="placeSelect"]').prop('checked', false);
                }
            });
        }

        // Enable the places on the child route.
        const $childPlaceList = $placeListContainer.find('li.placeSelectChildRouteItem').filter(`[data-child-route-id="${childRouteId}"]`);
        if($childPlaceList.length > 0) {
            $childPlaceList.each(function() {
                const $childPlaces = $(this).find('li.placeSelectSingleItem');
                $childPlaces.each(function() {
                    const $place = $(this);
                    $place.removeClass('place-disabled');
                    $place.find('label.placeSelectSingleItemLabel input[name="placeSelect"]').removeAttr('disabled');
                });
            });
        }
    } else {
        // Enable the shadow places on the primary route.
        const $shadowPlaces = $placeListContainer.find('li.placeSelectSingleItem.hasShadowRoute').filter(`[data-shadow-route-id="${childRouteId}"]`);
        if($shadowPlaces.length > 0) {
            $shadowPlaces.each(function() {
                const $place = $(this);
                $place.removeClass('place-disabled');
                $place.find('label.placeSelectSingleItemLabel input[name="placeSelect"]').removeAttr('disabled');
            });
        }

        // Disable the places on the child route.
        const $childPlaceList = $placeListContainer.find('li.placeSelectChildRouteItem').filter(`[data-child-route-id="${childRouteId}"]`);
        if($childPlaceList.length > 0) {
            $childPlaceList.each(function() {
                const $childPlaces = $(this).find('li.placeSelectSingleItem');
                $childPlaces.each(function() {
                    const $place = $(this);
                    $place.addClass('place-disabled');
                    $place.find('label.placeSelectSingleItemLabel input[name="placeSelect"]').attr('disabled', 'disabled');

                    // If any of the child places are selected, unselect them.
                    if($place.find('label.placeSelectSingleItemLabel input[name="placeSelect"]').is(':checked')) {
                        $place.find('label.placeSelectSingleItemLabel input[name="placeSelect"]').prop('checked', false);
                    }
                });
            });
        }
    }

    // 2. UPDATE RELATIVE DISTANCES FROM THE ORIGIN.
    if(listType === 'destination' || listType === 'stoplist') {
        // Get the cached route places (to lookup baseline distances from the origin).
        const cacheKey = $placeListContainer.attr('data-cache-key');
        const cachedRoutePlaces = getGlobalPathStops(cacheKey);
        log('cachedRoutePlaces: ', cachedRoutePlaces);
        log('placeListContainer: ', $placeListContainer);
        if(cachedRoutePlaces) {
            // Loop through every place in the list and update the relative distances from the origin.
            let runningCheckedViaDiffs = 0;
            $placeListContainer.children('li').each(function(index, li) {
                
                // Compare indexes.
                // log('compare indexes: ',index, li.getAttribute('data-place-id'), cachedRoutePlaces[index]?.id, cachedRoutePlaces[index]) //compare indexes

                if(li.classList.contains('placeSelectChildRouteItem')) {
                    // Skip the first place if it's a SPLIT. Must mean that origin is on this child route. So we don't want to add its distance to the diff.
                    // But also, we can skip updating it, since there's no possible toggles to check above it, it will never change from baseline.
                    if(index > 0) {
                        // Update children distances.
                        // Update by ID rather than index.
                        const childrenContainer = li.querySelector('ul[data-label="child-route-place-list-container"]');
                        if (childrenContainer) {
                            // Loop through all children of the data array.
                            $.each(cachedRoutePlaces[index]['children'], function(childIndex, childPlace) {
                                const childLi = childrenContainer.querySelector(`li[data-place-id="${childPlace.id}"]`);
                                if(childLi) {
                                    const baselineDistance = childPlace.baselineDistance;
                                    childLi.querySelector('[data-label="place-distance"]').textContent = displayLongDistance(baselineDistance + runningCheckedViaDiffs);
                                }
                            });
                        }

                        // Update running checked child route diffs.
                        // Note: We do this AFTER updating the children distances so we don't include the differential distance in the children distances.

                        // If the toggle is checked, add the differential distance to the running total.
                        if(li.querySelector('input[data-label="child-route-toggle"]')?.checked) {
                            runningCheckedViaDiffs += cachedRoutePlaces[index].childDistDiff;
                            // checkedViaIds.push(cachedRoutePlaces[index].childRouteId);
                        }
                    }
                } else if(li.classList.contains('placeSelectSingleItem')) {
                    // Get baseline distance from the cached route places.
                    const baselineDistance = cachedRoutePlaces[index].baselineDistance;
                    li.querySelector('[data-label="place-distance"]').textContent = displayLongDistance(baselineDistance + runningCheckedViaDiffs);
                }
            });
        }
    }

    // Update the page (either map or planner stage) when a toggle is changed in stoplist.
    // Get all the selected via ids.
    const selectedViaIds = $placeListContainer.find('li.placeSelectChildRouteItem label input[name="selectedChildRoutes"]:checked').map(function() {
        return $(this).val();
    }).get();
    log('selectedViaIds: ', selectedViaIds);

    // Get route slugs from the selected via ids.
    const selectedViaSlugs = selectedViaIds.map(viaId => getRouteById(viaId).slug);
    log('selectedViaSlugs: ', selectedViaSlugs);

    if(listType === 'stoplist' && placement === 'map') {
        // Update the via query param.
        updateQueryParam('via', selectedViaSlugs.join(','), false);
        // Update the map manually so we can set updateMapSidebar to false and not reset the stoplist
        updateMapFromUrl(false, false);
    }
});

/**
 * Filters out the places that are in the shadow of the origin if it's on a child route.
 * @param {Array} routePlaces - The route places to filter.
 * @param {integer} routeId - The ID of the route.
 * @param {integer} originId - The ID of the origin place.
 * @returns {Array} The filtered route places.
 */
export function filterInaccessiblePlaces(routePlaces, routeId, originId) {
    // Check if origin is on a child route.
    const originIndexOnRoute = getPlaceIndexAlongRoute(routeId, originId);
    if(originIndexOnRoute === false) {
        // Origin not on primary route. Check child routes.
        const originChildRouteId = getChildRoutePlaceIsAlong(routeId, originId);
        if(originChildRouteId === false) {
            // Origin not on any route. Shouldnt happen.
            return routePlaces;
        }

        // Origin is on a child route.
        
        // Loop through routePlaces, mark as hidden any place in the shadow of the origin child route.
        let found = false;
        $.each(routePlaces, function(index, routePlace) {
            if(routePlace.hasOwnProperty('shadowId') && routePlace.shadowId === originChildRouteId) {
                routePlace.hidden = true;
                found = true;
            } else {
                if(found) {
                    // Shadow places were found, and now we hit a place that's not in the shadow. So we must be finished. End the loop here.
                    return false;
                }
            }
        });
    }

    return routePlaces;
}

/**
 * Adds calculated values from the origin to the route places.
 * Sets the BASELINE values for the route places. These are the values without any optional vias selected. Only using child routes from the origin/destination places.
 * @param {Array} routePlaces - The route places to add calculated values to.
 * @param {integer} routeId - The ID of the route.
 * @param {integer} originId - The ID of the origin place.
 * @returns {Array} The route places with calculated values.
 */
export function addCalculatedValuesFromOrigin(routePlaces, routeId, originId) {
    routeId = parseInt(routeId);
    originId = parseInt(originId);
    
    // Set defaults
    let originDistance = 0;
    let originAscending = 0;
    let originDescending = 0;

    // Child route defaults
    let splitToOriginDistance = 0;
    let splitToOriginAscending = 0;
    let splitToOriginDescending = 0;

    // Child route differentials
    let originChildRouteDistDiff = 0;
    let originChildRouteAscDiff = 0;
    let originChildRouteDescDiff = 0;

    // Placeholders
    let originChildRouteId = false;
    let originPlaceOnChildRoute = false;

    // Get the index of the origin place in the routePlaces.
    const originIndex = routePlaces.findIndex(pl => pl.id === originId);
    if(originIndex > -1) {
        // OriginId found on primary route!
        originDistance = routePlaces[originIndex].dist;
        originAscending = routePlaces[originIndex].asc;
        originDescending = routePlaces[originIndex].desc;
    } else {
        // OriginId not found on primary route. Check child routes.
        
        // Loop through routePlaces and check each place's children property for the originId.
        $.each(routePlaces, function(index, place) {
            if(place.hasOwnProperty('children') && place.children.length > 0) {
                const childIndex = place.children.findIndex(c => c.id === originId);
                if(childIndex > -1) {
                    // OriginId found on child route!
                    log('originId found on child route: ', place.childRouteId);
                    originChildRouteId = place.childRouteId;
                    originPlaceOnChildRoute = place.children[childIndex];
                    const splitPlaceOnPrimaryRoute = place;
                    
                    // Use the SPLIT place on the primary route to get the distance, ascending, and descending values.                    
                    originDistance = splitPlaceOnPrimaryRoute.dist;
                    originAscending = splitPlaceOnPrimaryRoute.asc;
                    originDescending = splitPlaceOnPrimaryRoute.desc;
                    
                    // Set the values from the SPLIT place on the child route to the ORIGIN (on the child route).
                    splitToOriginDistance = originPlaceOnChildRoute.distFromStart;
                    splitToOriginAscending = originPlaceOnChildRoute.ascFromStart;
                    splitToOriginDescending = originPlaceOnChildRoute.descFromStart;

                    // Set the child route's differentials.
                    // Why? Bcz we're calculating the baseline values from the SPLIT on the primary route (BEFORE the origin); but those numbers ASSUME we're sticking to the primary route. So we need to normalize it to the child route.
                    originChildRouteDistDiff = splitPlaceOnPrimaryRoute.childDistDiff;
                    originChildRouteAscDiff = splitPlaceOnPrimaryRoute.childAscDiff;
                    originChildRouteDescDiff = splitPlaceOnPrimaryRoute.childDescDiff;

                    return false;
                }
            }
        });
       
    }

    // Now loop through routePlaces and add the calculated values to each place.
    $.each(routePlaces, function(index, place) {
        // Places along the primary route.
        place.baselineDistance = originDistance - place.dist - splitToOriginDistance + originChildRouteDistDiff;
        place.baselineAsc = originAscending - place.asc - splitToOriginAscending + originChildRouteAscDiff;
        place.baselineDesc = originDescending - place.desc - splitToOriginDescending + originChildRouteDescDiff;

        // Now loop through the child places of this place if there are any.
        if(place.hasOwnProperty('children') && place.children.length > 0 && place.hasOwnProperty('childRouteId')) {
            $.each(place.children, function(index, childPlace) {
                //Check if this child route is the same as the origin child route.
                if(place.childRouteId === originChildRouteId) {
                    // If so, just take the simple difference between the origin and this place along the same child route.
                    childPlace.baselineDistance = originPlaceOnChildRoute.dist - childPlace.dist;
                    childPlace.baselineAsc = originPlaceOnChildRoute.asc - childPlace.asc;
                    childPlace.baselineDesc = originPlaceOnChildRoute.desc - childPlace.desc;
                } else {
                    // We need to do same as if we're on the primary route to THIS split + the distance etc on THIS CHILD ROUTE.
                    childPlace.baselineDistance = (originDistance - place.dist - splitToOriginDistance + originChildRouteDistDiff) + childPlace.distFromStart;
                    childPlace.baselineAsc = (originAscending - place.asc - splitToOriginAscending + originChildRouteAscDiff) + childPlace.ascFromStart;
                    childPlace.baselineDesc = (originDescending - place.desc - splitToOriginDescending + originChildRouteDescDiff) + childPlace.descFromStart;
                }
            });
        }
    });

    return routePlaces;
}

